import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import {
  Typography, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Alert, Chip, Box,
} from '@mui/material';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { useDataProvider } from 'react-admin';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import type { EmployeeSearchResult } from '@/components/employee';
import { usePriorContext } from '@/components/employee/usePriorContext';
import { buildTransitionChain, getMachineNextState, useRuleEvaluation } from '@/components/workflow';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
}

const STEPS = [
  { id: 'incidentDetails', title: 'Incident Details' },
  { id: 'employeesInvolved', title: 'Employees Involved' },
  { id: 'annexure1', title: 'Annexure 1 Fields' },
  { id: 'investigation', title: 'Investigation' },
  { id: 'dolReporting', title: 'DOL Reporting' },
  { id: 'closeIncident', title: 'Close Incident' },
];

const INCIDENT_TYPES = ['Injury', 'Disease', 'Near Miss'];
const BODY_PARTS = ['Head', 'Neck', 'Torso', 'Back', 'Arm', 'Hand', 'Finger', 'Leg', 'Knee', 'Foot', 'Toe', 'Eye', 'Multiple'];
const CAUSE_TYPES = ['Unsafe Act', 'Unsafe Condition', 'Both', 'Other'];
const STATUS_OPTIONS = ['Open', 'Under Investigation', 'Resolved', 'Closed'];

const PPE_OPTIONS = [
  'Safety Boots',
  'Hard Hat',
  'Hi-Vis Vest',
  'Safety Glasses',
  'Gloves',
  'Ear Plugs',
  'Face Shield',
  'Respirator',
  'Fall Harness',
  'Coveralls',
  'Apron',
  'Shin Guards',
];

interface FormValues {
  incidentDate: string;
  incidentTime: string;
  location: string;
  incidentType: string;
  description: string;
  employeeName: string;
  employeeId: string;
  natureOfInjury: string;
  partOfBody: string;
  causeOfInjury: string;
  unsafeActs: string;
  unsafeConditions: string;
  protectiveEquipment: string[];
  witnessNames: string;
  statements: string;
  rootCause: string;
  correctiveActions: string;
  dolReportable: string;
  dolReportDate: string;
  fatality: string;
  status: string;
}

const INITIAL_VALUES: FormValues = {
  incidentDate: '', incidentTime: '', location: '', incidentType: '', description: '',
  employeeName: '', employeeId: '', natureOfInjury: '', partOfBody: '', causeOfInjury: '',
  unsafeActs: '', unsafeConditions: '', protectiveEquipment: [] as string[], witnessNames: '',
  statements: '', rootCause: '', correctiveActions: '', dolReportable: '', dolReportDate: '',
  fatality: '', status: 'Open',
};

const stepSchemas = {
  incidentDetails: z.object({
    incidentDate: z.string().min(1, 'Incident date is required'),
    incidentTime: z.string().min(1, 'Incident time is required'),
    location: z.string().trim().min(1, 'Location is required'),
    incidentType: z.string().min(1, 'Incident type is required'),
    description: z.string().trim().min(1, 'Description is required'),
  }),
  employeesInvolved: z.object({
    employeeName: z.string().trim().min(1, 'At least one employee name is required'),
    employeeId: z.string().trim().min(1, 'Employee ID is required'),
  }),
  annexure1: z.object({
    natureOfInjury: z.string().trim().min(1, 'Nature of injury is required (Annexure 1)'),
    partOfBody: z.string().min(1, 'Part of body affected is required (Annexure 1)'),
    causeOfInjury: z.string().min(1, 'Cause of injury is required (Annexure 1)'),
  }),
  investigation: z.object({
    rootCause: z.string().trim().min(1, 'Root cause analysis is required'),
  }),
  dolReporting: z.object({
    dolReportable: z.string().min(1, 'DOL reportable status is required'),
    dolReportDate: z.string().optional(),
  }).refine(
    (data) => data.dolReportable !== 'Yes' || data.dolReportDate,
    { message: 'DOL report date is required when incident is reportable', path: ['dolReportDate'] },
  ),
  closeIncident: z.object({}),
};

function validateStep(stepId: string, values: FormValues) {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  return stepValidator(schema)(values as unknown as Record<string, unknown>);
}

export const InjuryWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);

  const priorData = usePriorContext({ dataProvider, employeeId: selectedEmployee?.employeeNumber || '' });

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      incidentId: null,
      employeeId: v.employeeId,
      incidentDate: v.incidentDate,
      incidentTime: v.incidentTime,
      incidentLocation: v.location,
      incidentType: v.incidentType,
      incidentDescription: v.description,
      witnesses: v.witnessNames ? v.witnessNames.split(',').map((s: string) => s.trim()) : [],
      natureOfInjury: v.natureOfInjury,
      bodyPartAffected: v.partOfBody,
      causeOfInjury: v.causeOfInjury,
      unsafeActsNoted: v.unsafeActs ? [v.unsafeActs] : [],
      unsafeConditionsNoted: v.unsafeConditions ? [v.unsafeConditions] : [],
      protectiveEquipmentWorn: v.protectiveEquipment.length > 0,
      protectiveEquipmentUsed: v.protectiveEquipment,
      medicalTreatmentProvided: false,
      medicalTreatmentDetails: null,
      hospitalizationDuration: 0,
      amputationOccurred: (v.natureOfInjury || '').toLowerCase().includes('amputation'),
      dangerousOccurrence: false,
      investigatorId: null,
      investigationFindings: v.rootCause,
      rootCauses: v.rootCause ? [v.rootCause] : [],
      correctiveActions: v.correctiveActions ? [v.correctiveActions] : [],
      preventiveMeasures: [],
      dolReported: v.dolReportable === 'Yes',
      dolReportingTimeframe: v.dolReportable === 'Yes' ? '7_days' : null,
      dolReportedAt: v.dolReportDate || null,
    };

    const incidentResult = await dataProvider.create('workplace-injuries', { data: formValues });
    const incidentId = incidentResult.data.id;

    const wizardEvents = ['CAPTURE_ANNEXURE', 'ANNEXURE_COMPLETE', 'NO_TREATMENT_REQUIRED', 'RECORD_FINDINGS'];
    const { finalState: baseState, transitions: baseTransitions } = buildTransitionChain('ohsincident', 'reported', wizardEvents);

    let currentState = baseState;
    const transitions = [...baseTransitions];

    const finalEvent = v.dolReportable === 'Yes' ? 'SUBMIT_DOL' : 'CLOSE_NO_DOL';
    const finalNext = getMachineNextState('ohsincident', currentState, finalEvent);
    if (finalNext) {
      transitions.push({
        sequence: transitions.length + 1,
        event: finalEvent,
        fromState: currentState,
        toState: finalNext,
        timestamp: new Date().toISOString(),
        triggeredBy: 'current-user',
      });
      currentState = finalNext;
    }

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'ohsincident',
        entityId: incidentId,
        entityName: 'Incident: ' + v.employeeName,
        currentState: { value: currentState, context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: incidentId,
        transitions,
      },
    });

    return { id: incidentId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepHook,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: {
      ...INITIAL_VALUES,
      employeeId: employeeId || '',
    },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'ohs-injury-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      employeeId: (values as unknown as FormValues).employeeId,
      incidentType: (values as unknown as FormValues).incidentType,
      dolReportable: (values as unknown as FormValues).dolReportable,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'closeIncident') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const vals = values as unknown as FormValues;

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);
    const fields: PrefilledField[] = [
      {
        field: 'department',
        label: 'Department',
        sourceValue: employee.organisationalHierarchy?.department || 'Not available',
        currentValue: '',
        confirmed: false,
      },
    ];
    setPrefilledFields(fields);
    updateValues({
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    });
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
    updateValues({
      employeeName: '',
      employeeId: '',
    });
  };

  const checkIncidentAge = (): { warning: boolean; message: string } | null => {
    if (!vals.incidentDate) return null;
    const incidentDateObj = new Date(vals.incidentDate);
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - incidentDateObj.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 7) {
      return { warning: true, message: `Warning: Incident date is ${daysDiff} days old. OHS Act requires reporting within 7 days.` };
    }
    return null;
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, vals);
    if (!result.isValid) {
      return;
    }

    if (currentStep.id === 'incidentDetails') {
      const ageWarning = checkIncidentAge();
      if (ageWarning) {
        return;
      }
    }

    if (currentStep.id === 'dolReporting' && vals.dolReportable === 'Yes') {
      return;
    }

    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleComplete = async () => {
    await complete();
  };

  const handleReset = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
    reset();
  };

  const renderFormFields = (stepId: string): React.ReactNode => {
    const vals = values as unknown as FormValues;
    switch (stepId) {
      case 'incidentDetails':
        return (
          <>
            <TextField
              label="Incident Date"
              type="date"
              value={vals.incidentDate}
              onChange={e => updateField('incidentDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.incidentDate}
              helperText={errors.incidentDate || 'Date when incident occurred'}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Incident Time"
              type="time"
              value={vals.incidentTime}
              onChange={e => updateField('incidentTime', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.incidentTime}
              helperText={errors.incidentTime}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Location"
              value={vals.location}
              onChange={e => updateField('location', e.target.value)}
              fullWidth
              error={!!errors.location}
              helperText={errors.location || 'Specific location where incident occurred'}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth error={!!errors.incidentType} sx={{ mb: 2 }}>
              <InputLabel>Incident Type</InputLabel>
              <Select
                value={vals.incidentType}
                label="Incident Type"
                onChange={e => updateField('incidentType', e.target.value)}
              >
                {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
              {errors.incidentType && <Typography variant="caption" color="error">{errors.incidentType}</Typography>}
            </FormControl>
            <TextField
              label="Incident Description"
              value={vals.description}
              onChange={e => updateField('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description || 'Detailed description of how the incident occurred'}
              sx={{ mb: 2 }}
            />
          </>
        );

      case 'employeesInvolved':
        return (
          <>
            <Grid size={{ xs: 12 }}>
              <EmployeeSearchAutocomplete
                onEmployeeSelect={handleEmployeeSelect}
                onClear={handleClearEmployee}
              />
            </Grid>
            {prefilledFields.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Employee Info:</strong> {selectedEmployee?.fullName} ({selectedEmployee?.employeeNumber})
                  </Typography>
                  {prefilledFields.map(field => (
                    <Typography key={field.field} variant="body2">
                      {field.label}: {String(field.sourceValue)}
                    </Typography>
                  ))}
                </Alert>
              </Grid>
            )}
            {priorData.priorIncidents.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This employee has {priorData.priorIncidents.length} prior OHS incident(s)
                </Alert>
              </Grid>
            )}
            <TextField
              label="Witness Names"
              value={vals.witnessNames}
              onChange={e => updateField('witnessNames', e.target.value)}
              fullWidth
              placeholder="Names of any witnesses present"
              helperText="Optional - names of witnesses"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Witness Statements"
              value={vals.statements}
              onChange={e => updateField('statements', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Summary of witness accounts"
              sx={{ mb: 2 }}
            />
          </>
        );

      case 'annexure1':
        return (
          <>
            <TextField
              label="Nature of Injury"
              value={vals.natureOfInjury}
              onChange={e => updateField('natureOfInjury', e.target.value)}
              fullWidth
              error={!!errors.natureOfInjury}
              helperText={errors.natureOfInjury || 'Describe the nature of injury (Annexure 1 required field)'}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth error={!!errors.partOfBody} sx={{ mb: 2 }}>
              <InputLabel>Part of Body Affected</InputLabel>
              <Select
                value={vals.partOfBody}
                label="Part of Body Affected"
                onChange={e => updateField('partOfBody', e.target.value)}
              >
                {BODY_PARTS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
              {errors.partOfBody && <Typography variant="caption" color="error">{errors.partOfBody}</Typography>}
            </FormControl>
            <FormControl fullWidth error={!!errors.causeOfInjury} sx={{ mb: 2 }}>
              <InputLabel>Cause of Injury</InputLabel>
              <Select
                value={vals.causeOfInjury}
                label="Cause of Injury"
                onChange={e => updateField('causeOfInjury', e.target.value)}
              >
                {CAUSE_TYPES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
              {errors.causeOfInjury && <Typography variant="caption" color="error">{errors.causeOfInjury}</Typography>}
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel shrink>Protective Equipment Used</InputLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 2 }}>
                {PPE_OPTIONS.map(equipment => {
                  const isSelected = vals.protectiveEquipment.includes(equipment);
                  return (
                    <Chip
                      key={equipment}
                      label={equipment}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => {
                        const current = vals.protectiveEquipment;
                        const updated = isSelected
                          ? current.filter(e => e !== equipment)
                          : [...current, equipment];
                        updateField('protectiveEquipment', updated);
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' },
                      }}
                    />
                  );
                })}
              </Box>
            </FormControl>
          </>
        );

      case 'investigation':
        return (
          <>
            <TextField
              label="Unsafe Acts Identified"
              value={vals.unsafeActs}
              onChange={e => updateField('unsafeActs', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Describe any unsafe acts that contributed to the incident"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Unsafe Conditions Identified"
              value={vals.unsafeConditions}
              onChange={e => updateField('unsafeConditions', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Describe any unsafe conditions that contributed to the incident"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Root Cause Analysis"
              value={vals.rootCause}
              onChange={e => updateField('rootCause', e.target.value)}
              fullWidth
              multiline
              rows={3}
              error={!!errors.rootCause}
              helperText={errors.rootCause || 'Identify the root cause of the incident (required)'}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Corrective Actions"
              value={vals.correctiveActions}
              onChange={e => updateField('correctiveActions', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Steps to prevent recurrence"
              sx={{ mb: 2 }}
            />
          </>
        );

      case 'dolReporting':
        return (
          <>
            <FormControl fullWidth error={!!errors.dolReportable} sx={{ mb: 2 }}>
              <InputLabel>DOL Reportable (OHS Act Section 24)</InputLabel>
              <Select
                value={vals.dolReportable}
                label="DOL Reportable (OHS Act Section 24)"
                onChange={e => updateField('dolReportable', e.target.value)}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.dolReportable && <Typography variant="caption" color="error">{errors.dolReportable}</Typography>}
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Fatality</InputLabel>
              <Select
                value={vals.fatality}
                label="Fatality"
                onChange={e => updateField('fatality', e.target.value)}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
            {vals.dolReportable === 'Yes' && (
              <TextField
                label="DOL Report Date"
                type="date"
                value={vals.dolReportDate}
                onChange={e => updateField('dolReportDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.dolReportDate}
                helperText={errors.dolReportDate || 'Date when DOL was notified'}
                sx={{ mb: 2 }}
              />
            )}
          </>
        );

      case 'closeIncident':
        return (
          <>
            {ruleResult && !ruleResult.allowed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Guard Check</Typography>
                <Typography variant="body2">{ruleResult.reason}</Typography>
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Incident Status</InputLabel>
              <Select
                value={vals.status}
                label="Incident Status"
                onChange={e => updateField('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        );

      default:
        return null;
    }
  };

  const getStepDescription = (stepId: string): string => {
    const descriptions: Record<string, string> = {
      incidentDetails: 'Capture the date, time, location and basic details of the workplace incident',
      employeesInvolved: 'Record information about the affected employee(s)',
      annexure1: 'Complete mandatory Annexure 1 fields required under OHS Act Section 24',
      investigation: 'Document the investigation findings and root cause analysis',
      dolReporting: 'Determine DOL (Department of Labour) reportability requirements',
      closeIncident: 'Finalize the incident record and set closure status',
    };
    return descriptions[stepId] || '';
  };

  if (isCompleted) {
    return (
      <WizardLayout
        steps={STEPS}
        currentStepIndex={currentStepIndex}
        progress={progress}
        isSaving={isSaving}
        isCompleted={isCompleted}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onReset={handleReset}
      >
        <OutcomeSummary
          title="Workplace Injury Reported"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee', value: vals.employeeName },
            { label: 'Employee #', value: vals.employeeId },
            { label: 'Incident Date', value: vals.incidentDate },
            { label: 'Incident Type', value: vals.incidentType },
            { label: 'Location', value: vals.location },
            { label: 'DOL Reportable', value: vals.dolReportable || 'No' },
            { label: 'Protective Equipment', value: vals.protectiveEquipment.length > 0 ? vals.protectiveEquipment.join(', ') : 'None' },
            { label: 'Status', value: vals.status },
          ]}
          footerActions={[
            { label: 'View Incidents', to: '/workplaceInjuries' },
            { label: 'Report Another', onClick: handleReset },
          ]}
        />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onNext={handleNext}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
      <WizardStep
        stepNumber={currentStepIndex + 1}
        totalSteps={totalSteps}
        title={currentStep.title}
        description={getStepDescription(currentStep.id)}
        feedback={stepFeedback}
      >
        {renderFormFields(currentStep.id)}
      </WizardStep>
    </WizardLayout>
  );
};